import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import api from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';

interface Rental {
  id: string;
  assetId: number;
  tenantName: string;
  tenantEmail?: string | null;
  tenantPhone?: string | null;
  monthlyRent: number;
  paymentDay?: number | null;
  startDate?: string;
  endDate?: string | null;
  status: 'active' | 'terminated';
  assetDescription?: string;
}

const Rentals: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: rentals, isLoading, error } = useQuery<Rental[]>(
    'rentals',
    async () => (await api.get('/rentals')).data,
    { staleTime: 1000 * 60 * 5, refetchOnMount: 'always' }
  );

  const filtered = useMemo(() => {
    return (rentals || []).filter(r => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        (r.tenantName || '').toLowerCase().includes(q) ||
        String(r.assetId).includes(q);
      const matchesStatus = selectedStatus === 'all' || r.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });
  }, [rentals, searchTerm, selectedStatus]);

  const monthly = filtered
    .filter(r => r.status === 'active')
    .reduce((s, r) => s + Number(r.monthlyRent || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar aluguéis.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Aluguéis"
        title="Aluguéis"
        subtitle="Contratos de locação"
        actions={
          hasPermission('assets.update') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/rentals/new')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo aluguel
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select
              className="select"
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Ativo</option>
              <option value="terminated">Encerrado</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Inquilino ou imóvel"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Contratos</div>
          <div className="kpi-value">{filtered.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Receita mensal</div>
          <div className="kpi-value positive">{formatCurrency(monthly)}</div>
          <div className="kpi-hint">contratos ativos</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Ativos</div>
          <div className="kpi-value">
            {(rentals || []).filter(r => r.status === 'active').length}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Aluguéis</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhum aluguel encontrado.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Inquilino</th>
                  <th className="hide-m">Imóvel / bem</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m">Venc. dia</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td>
                      <StackCell
                        title={r.tenantName}
                        meta={r.tenantEmail || undefined}
                        stackExtra={
                          <>
                            {r.assetDescription || `Bem #${r.assetId}`} · {formatStatus(r.status)}
                            {r.paymentDay ? ` · dia ${r.paymentDay}` : ''} · {formatCurrency(Number(r.monthlyRent || 0))}
                          </>
                        }
                      />
                    </td>
                    <td className="hide-m">{r.assetDescription || `Bem #${r.assetId}`}</td>
                    <td className="hide-m">
                      <StatusChip
                        label={formatStatus(r.status)}
                        tone={r.status === 'active' ? 'green' : 'slate'}
                      />
                    </td>
                    <td className="hide-m mono">{r.paymentDay || '—'}</td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(Number(r.monthlyRent || 0))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/rentals/${r.id}`)} />
                        {hasPermission('assets.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(r.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir Aluguel"
        message="Tem certeza que deseja excluir este aluguel? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await api.delete(`/rentals/${deleteId}`);
            queryClient.invalidateQueries(['rentals']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Rentals;
